import { CubicBezier, } from "../../../d3-mapp/curve/bezier.js";
import { CubicCubicIntersections } from "../../../d3-mapp/curve/intersection.js";
export const list = [
    {
        id: 4,
        beer: "Alfa",
    },
];
/**
 * Extract all section where the given spline (by it's index) is highest/lowest
 * in the given profile and tag that section with given tag.
 * @param profile The profile return by findIntersections
 * @param splineIndex the index of the spline in question
 * @param checkHighest check for being the highest or the lowest
 * @param tag the tag to apply to the sections; defaults to 'highest'
 * @returns the tagged sections where the spline is highest/lowest in the profile
 */
export const getTaggedSectionsWhereSplineIsHighestLowest = (profile, splineIndex, checkHighest = true, tag = "highest") => {
    const index = checkHighest ? -1 : 0;
    return profile.reduce((highLowTagSections, _interval, i) => {
        if (i == 0) {
            //skip the first interval
        }
        else {
            if (profile[i - 1].order.at(index)?.spline === splineIndex) {
                //the spline of interest is the highest/lowest in current interval
                //add or extend previous section
                const start = profile[i - 1].x;
                const end = profile[i].x;
                const previousSection = highLowTagSections.length >= 0
                    ? highLowTagSections.at(-1)
                    : undefined;
                if (previousSection && previousSection.end === start) {
                    //extend previous section
                    previousSection.end = end;
                }
                else {
                    highLowTagSections.push({ start, end, tag });
                }
            }
        }
        return highLowTagSections;
    }, []);
};
/**
 * Returns for each interval a tag corresponding to the spline who
 * is highest/lowest for that interval. This can be used in
 * projection and histogram visuals
 * @param profile The profile return by findIntersections
 * @param checkHighest flag indicating what to return (highest vs lowest)
 * @returns the list of intervals
 */
export const getProjectionData = (profile, checkHighest = true) => {
    const index = checkHighest ? -1 : 0;
    return profile.reduce((projectionData, _interval, i) => {
        if (i == 0) {
            //skip the first interval
        }
        else {
            if (profile[i - 1].order.length > 0) {
                const start = profile[i - 1].x;
                const end = profile[i].x;
                const tag = `_${profile[i - 1].order.at(index)?.spline}`;
                const previousDatum = projectionData.length >= 0 ? projectionData.at(-1) : undefined;
                if (previousDatum &&
                    previousDatum.end === start &&
                    previousDatum.tag === tag) {
                    //extend previous section
                    previousDatum.end = end;
                }
                else {
                    projectionData.push({ start, end, tag });
                }
            }
        }
        return projectionData;
    }, []);
};
export const findIntersections = (splines) => {
    const splineIntersections = [];
    if (!splines || splines.length === 0)
        return splineIntersections;
    //for every spline, find its first curve
    const positions = firstCurves(splines);
    //choose among the available positions the one that has the smallest x / starts first
    let mainCurveIndex = firstCurve(splines, positions);
    //prevent us from searching the complete list of splineIntersections so far
    let from = 0;
    let step = 0;
    //for as long we got spline sections left look for intersection and keep track of their (stacking) order
    while (mainCurveIndex) {
        //get the main curve
        const curve0 = curve(splines, mainCurveIndex);
        if (curve0) {
            const compareSpline = mainCurveIndex.spline;
            //get start x form main curve
            const xs0 = CubicBezier.getStart(curve0).x;
            const xe0 = CubicBezier.getEnd(curve0).x;
            //add the curveIndex to the profile
            from = processCurve(mainCurveIndex, xs0, xe0, splineIntersections, from);
            //get all curves from the other splines that overlap with our main curve
            const overlappingCurveIndices = otherOverlappingCurves(splines, positions, mainCurveIndex);
            // console.log(
            //   `START STEP ${step} with main interval ${mainCurveIndex.spline}_${mainCurveIndex.section}_${mainCurveIndex.curve}`
            // );
            // logSplineIntersections(splineIntersections, true);
            //loop over all these overlapping other spline curves
            overlappingCurveIndices.forEach((overlappingCurveIndex, subStep) => {
                //get the overlapping bezier curve
                const curve1 = curve(splines, overlappingCurveIndex);
                if (curve1) {
                    //the overlapping curve exists
                    //calculate the intersection between these to bezier curves and determine their stacking order
                    const intersectionResult = getIntersection(curve0, curve1, mainCurveIndex, overlappingCurveIndex);
                    //only process these parts where the two curves overlap
                    const intersectionStack = intersectionResult.stacking.filter((stackingInterval) => stackingInterval.stack.length == 2);
                    //iterate over the stacking order for these two spline curves
                    intersectionStack.forEach((stackingInterval, i) => {
                        //}, stackIndex) => {
                        //get the start & end  x of the interval
                        const xs = stackingInterval.start;
                        const xe = stackingInterval.end;
                        //determine if the overlapping curve is above or under the main curve within this interval
                        const overlappingCurveIsAbove = stackingInterval.stack[0].spline === compareSpline;
                        //is this the last on the intersection stack?
                        const isLast = i === intersectionStack.length - 1;
                        processCurve(overlappingCurveIndex, xs, xe, splineIntersections, from, compareSpline, overlappingCurveIsAbove, isLast, intersectionResult);
                    });
                }
                // console.log(
                //   `STEP ${step}-${subStep} with overlapping interval ${overlappingCurveIndex.spline}_${overlappingCurveIndex.section}_${overlappingCurveIndex.curve}`
                // );
                //logSplineIntersections(splineIntersections, true);
            });
        }
        else {
            //some how the curve could not be found: skip
        }
        //we can advance the earliestCurveIndex to the next curve on that spline
        positions[mainCurveIndex.spline] = nextCurve(splines, mainCurveIndex);
        //choose among the available positions the one that has the smallest x / starts first
        mainCurveIndex = firstCurve(splines, positions);
        step++;
    }
    //filter out all intervals except for the first, that do not bring chance in the order and have no intersections
    const result = splineIntersections.filter((interval, i) => {
        if (i == 0 || interval.intersections.length > 0)
            return true;
        let sameOrder = interval.order.length === splineIntersections[i - 1].order.length;
        let j = 0;
        while (sameOrder && j < interval.order.length) {
            const i0 = interval.order[j];
            const i1 = splineIntersections[i - 1].order[j++];
            sameOrder =
                i0.spline === i1.spline &&
                    i0.section === i1.section &&
                    i0.curve === i1.curve;
        }
        return !sameOrder;
    });
    //logging
    // console.log("FINAL RESULT");
    // logSplineIntersections(result, true);
    return result;
};
const logSplineIntersections = (splineIntersections, verbose = false) => {
    const xToString = verbose
        ? (x) => `${new Date(x).toISOString()} ${x.toFixed(0)}`
        : (x) => `${new Date(x).toISOString()}`;
    const indexT0String = verbose
        ? (i) => `${i.spline}_${i.section}_${i.curve}`
        : (i) => `${i.spline}`;
    let str = "";
    for (let i = 0; i < splineIntersections.length; i++) {
        const interval = splineIntersections[i];
        str += `${xToString(interval.x)}: ${interval.order.map((sci) => indexT0String(sci)).join(" | ")} ${interval.intersections.length > 0
            ? ` intersections: ${interval.intersections.map((intersection) => `{ splines: ${indexT0String(intersection.curves[0])} | ${indexT0String(intersection.curves[1])} y: ${intersection.y} }`).join(" ")}`
            : ""}\n`;
    }
    if (str.length > 0) {
        console.log("\n" + str);
    }
    else {
        console.log("no intersections/stacking profile");
    }
};
const getIntersection = (curveA, curveB, indexA = { spline: 0, section: 0, curve: 0 }, indexB = { spline: 1, section: 0, curve: 0 }) => {
    const testCurves = [curveA, curveB];
    const indices = [indexA, indexB];
    //curve that started before the other
    let profilePrefix = undefined;
    //curve that ended after the other
    let profilePostfix = undefined;
    //the index of the curve that started first (0= curveA; 1=curveB, undefined = both curves started simultaneously)
    let prefix = undefined;
    //the index of the curve that ended latert (0= curveA; 1=curveB, undefined = both curves ended simultaneously)
    let postfix = undefined;
    //make sure we only test where the curves overlap in time
    const start0 = CubicBezier.getStart(testCurves[0]).x;
    const start1 = CubicBezier.getStart(testCurves[1]).x;
    const end0 = CubicBezier.getEnd(testCurves[0]).x;
    const end1 = CubicBezier.getEnd(testCurves[1]).x;
    //first the start
    if (start0 < start1) {
        //first curve starts earlier then second curve
        //clip start of first curve
        const splittedCurve = CubicBezier.splitAtX(testCurves[0], start1);
        testCurves[0] = splittedCurve[1];
        prefix = 0;
        profilePrefix = { start: start0, end: start1, stack: [indices[prefix]] };
    }
    else if (start0 > start1) {
        //first curve starts later then second curve
        //clip start of second curve
        const splittedCurve = CubicBezier.splitAtX(testCurves[1], start0);
        testCurves[1] = splittedCurve[1];
        prefix = 1;
        profilePrefix = { start: start1, end: start0, stack: [indices[prefix]] };
    }
    //then the end
    if (end0 < end1) {
        //first curve ends earlier then second curve
        //clip end of second curve
        const splittedCurve = CubicBezier.splitAtX(testCurves[1], end0);
        testCurves[1] = splittedCurve[0];
        postfix = 1;
        profilePostfix = { start: end0, end: end1, stack: [indices[postfix]] };
    }
    else if (end0 > end1) {
        //first curve ends later then second curve
        //clip end of first curve
        const splittedCurve = CubicBezier.splitAtX(testCurves[0], end1);
        testCurves[0] = splittedCurve[0];
        postfix = 0;
        profilePostfix = { start: end1, end: end0, stack: [indices[postfix]] };
    }
    //calculate intersections where the curves overlap
    const ti = CubicCubicIntersections.findIntersectionsT(testCurves[0], testCurves[1]);
    //we want to keep track of the stacking order of the curves
    const stacking = [];
    //take all intersections save at t=0 and t=1
    const tProfile = ti.filter((t) => t > 0.0 && t < 1.0);
    //prepend these intersection t's with t=0
    tProfile.unshift(0.0);
    //append these intersections t's with t=1
    tProfile.push(1.0);
    let sp0 = CubicBezier.get(testCurves[0], tProfile[0]); //start of profile interval for curve 0
    let ep0; //end of profile interval for curve 0
    //loop over the constructed list of intersections
    for (let i = 1; i < tProfile.length; i++) {
        //update the end of the current interval
        ep0 = CubicBezier.get(testCurves[0], tProfile[i]);
        const tc0 = (tProfile[i] + tProfile[i - 1]) / 2.0; //center t of profile for curve 0
        const cp0 = CubicBezier.get(testCurves[0], tc0); // coordinate for this tc0 on curve 0
        const tc1 = CubicBezier.tForX(testCurves[1], cp0.x); // t for curve 1 where x = cp0.x (we can assume one t because x is monotonous increasing )
        const cp1 = CubicBezier.get(testCurves[1], tc1[0]); // coordinate for this tc1 on curve 1
        //cp0 and cp1 should have the same x (x = millis)
        // the values of cp0.y and cp1.y determine the order, stack[0] being the lowest
        const stack = cp0.y <= cp1.y ? [indices[0], indices[1]] : [indices[1], indices[0]];
        //add interval on stacking profile
        stacking.push({ start: sp0.x, end: ep0.x, stack });
        sp0 = ep0; //advance: make the start of the new interval equal to the end of the previous one
    }
    //build the end result
    const result = {
        //report back the curve 0 parameter values
        t0: ti,
        //report back the intersection coordinates
        points: ti.map((t) => CubicBezier.get(testCurves[0], t)),
        //the curves involved
        curves: indices,
        //the stacking order
        stacking,
    };
    if (profilePrefix) {
        result.stacking.unshift(profilePrefix);
    }
    if (profilePostfix) {
        result.stacking.push(profilePostfix);
    }
    return result;
};
/**
 * Returns the curve referenced by the given index.
 * @param splines The splines to look through
 * @param index The index of the curve to return
 * @returns The curve referenced by the index
 */
const curve = (splines, index) => {
    if (index && splines && index.spline >= 0 && index.spline < splines.length) {
        const spline = splines[index.spline];
        if (index.section >= 0 && index.section < spline.length) {
            const section = spline[index.section];
            if (index.curve >= 0 && index.curve < section.length) {
                return section[index.curve];
            }
        }
    }
    return undefined;
};
/**
 * Returns the index of the first curve for each given spline
 * @param splines The splines
 * @returns list of all first curves on every spline
 */
const firstCurves = (splines) => {
    if (splines && splines.length > 0) {
        return splines.map((spline, splineIndex) => {
            const index = { spline: splineIndex, section: -1, curve: -1 };
            if (spline.length > 0) {
                let sectionIndex = 0;
                while (sectionIndex < spline.length && index.section === -1) {
                    if (spline[sectionIndex].length > 0) {
                        index.section = sectionIndex;
                        index.curve = 0;
                    }
                    sectionIndex++;
                }
            }
            return index;
        });
    }
    else {
        return [];
    }
};
/**
 * Returns the index of the next curve following the given index.
 * @param splines The splines
 * @param index the index of the current curve
 * @returns The index of the curve following the curve with the given index.
 */
const nextCurve = (splines, index) => {
    const nextIndex = { spline: -1, section: -1, curve: -1 };
    if (index.spline >= 0 && index.spline < splines.length) {
        //index.spline points to an existing array of spline sections (i.e. a Spline)
        nextIndex.spline = index.spline;
        const spline = splines[index.spline];
        if (index.section >= 0 && index.section < spline.length) {
            //index.section points to an existing array of curves (i.e. a SplineSection)
            if (index.curve >= 0 && index.curve < spline[index.section].length - 1) {
                //we can simply increase the curve index
                nextIndex.section = index.section;
                nextIndex.curve = index.curve + 1;
            }
            else {
                //find next section that contains curves
                let sectionIndex = index.section + 1;
                while (sectionIndex < spline.length && nextIndex.section === -1) {
                    if (spline[sectionIndex].length > 0) {
                        nextIndex.section = sectionIndex;
                        nextIndex.curve = 0;
                    }
                    sectionIndex++;
                }
            }
        }
    }
    return nextIndex;
};
/**
 * Returns the curve with the earliest (i.e. lowest) t0 parameter among
 * the curves referenced by the given positions
 * @param splines The splines to look through
 * @param positions The indices of the curves to compare
 * @returns The index of the earliest curve or undefined when it does not exist.
 */
const firstCurve = (splines, positions) => {
    return positions.reduce((fi, ci) => {
        const fCurve = curve(splines, fi);
        const cCurve = curve(splines, ci);
        const firstT = fCurve ? CubicBezier.getStart(fCurve).x : undefined;
        const curveT = cCurve ? CubicBezier.getStart(cCurve).x : undefined;
        if (curveT !== undefined) {
            if (firstT !== undefined) {
                return firstT <= curveT ? fi : ci;
            }
            return ci;
        }
        return fi;
    }, undefined);
};
/**
 * Return all curve indices that is not the given index
 * @param positions The list of curve indices
 * @param index the curve index not to return
 * @returns all curve indices not equal to the given curve index.
 */
const otherCurves = (positions, index) => {
    return index
        ? positions.filter((position) => position.spline !== index.spline)
        : positions;
};
/**
 * Return all curve indices that is not the given index and where
 * the referenced curves have a (time) overlap with the curve
 * referenced by the given index.
 * @param positions The list of curve indices
 * @param index the curve index not to return
 * @returns all curve indices not equal to the given curve index with time overlap.
 */
const otherOverlappingCurves = (splines, positions, index) => {
    const bc = curve(splines, index);
    if (bc) {
        const ts = CubicBezier.getStart(bc).x;
        const te = CubicBezier.getEnd(bc).x;
        return otherCurves(positions, index).flatMap((otherIndex) => {
            const splineIndices = [];
            let nextIndex = otherIndex;
            let curveAndIndex = {
                index: nextIndex,
                curve: curve(splines, nextIndex),
            };
            while (curveAndIndex.curve &&
                CubicBezier.getStart(curveAndIndex.curve).x < te &&
                CubicBezier.getEnd(curveAndIndex.curve).x > ts) {
                splineIndices.push(curveAndIndex.index);
                nextIndex = nextCurve(splines, nextIndex);
                curveAndIndex = { index: nextIndex, curve: curve(splines, nextIndex) };
            }
            return splineIndices;
        });
    }
    return [];
};
const findProfileIntervalIndex = (x, profile, from = 0) => {
    if (profile.length == 0)
        return -1;
    const _from = Math.max(0, Math.min(from, profile.length - 1));
    let index = _from;
    if (profile[_from].x <= x) {
        //search in the tail
        while (index < profile.length && profile[index].x <= x)
            index++;
        return index - 1;
    }
    else {
        //search in the head
        while (index >= 0 && profile[index].x > x)
            index--;
        return index;
    }
};
/**
 * Returns the enumerated indices for all intervals valid for interval
 * [xs, xe) or [xs,xe], depending on the excludeFlag parameter.
 * Since each boundary must exist in the profile, boundaries for xs and xe
 * are automatically created when they did not exist prior to the call
 * to this method.
 *
 * @param xs The start x
 * @param xe The end x
 * @param profile The current profile
 * @param excludeEnd Flag indicating to exclude xe from the returned indices
 * @param from Used for optimization , defaults to 0.
 * @returns The indices of all intervals valid for interval [xs, xe)
 */
const getProfileIntervalIndices = (xs, xe, profile, excludeEnd, from = 0) => {
    if (profile.length == 0) {
        //profile is empty so just add the intervals
        profile.push({ x: xs, order: [], intersections: [] });
        profile.push({ x: xe, order: [], intersections: [] });
        return excludeEnd ? [0] : [0, 1];
    }
    let indexEnd = findProfileIntervalIndex(xe, profile, from);
    if (indexEnd == -1) {
        //interval for xe not found (so interval for xs does not exist either)
        profile.unshift({ x: xe, order: [], intersections: [] });
        profile.unshift({ x: xs, order: [], intersections: [] });
        return excludeEnd ? [0] : [0, 1];
    }
    //handle x start
    let indexStart = findProfileIntervalIndex(xs, profile, from);
    if (indexStart == -1) {
        profile.unshift({ x: xs, order: [], intersections: [] });
        indexStart = 0;
    }
    else {
        if (profile[indexStart].x != xs) {
            //we want exact matches so duplicate the interval, adjust the x to xs and insert
            //after the interval just found
            profile.splice(indexStart + 1, 0, {
                x: xs,
                order: profile[indexStart].order.map((ci) => Object.assign({}, ci)),
                intersections: [],
            });
            indexStart++;
            indexEnd++; //the end index shifts one up as well
        }
        else {
            //the interval is an exact match, no further work needed
        }
    }
    //now handle the x end
    if (profile[indexEnd].x != xe) {
        //we want exact matches so duplicate the interval, adjust the x to xe and insert
        //after the interval just found
        profile.splice(indexEnd + 1, 0, {
            x: xe,
            order: profile[indexEnd].order.map((ci) => Object.assign({}, ci)),
            intersections: [],
        });
        indexEnd++;
    }
    else {
        //the interval is an exact match, no further work needed
    }
    //now we return an array from indexStart to indexEnd
    const ext = excludeEnd ? 0 : 1;
    const indices = new Array(indexEnd - indexStart + ext);
    for (let i = indexStart; i < indexEnd + ext; i++) {
        indices[i - indexStart] = i;
    }
    return indices;
};
const isSplineCurve = (curveIndex) => {
    return curveIndex.spline !== undefined
        ? (ci) => ci.spline === curveIndex.spline &&
            ci.section === curveIndex.section &&
            ci.curve === curveIndex.curve
        : (ci) => ci.spline === curveIndex;
};
const processCurve = (curveIndex, start, end, profile, from = 0, compareSpline, curveIsAbove, isLast, intersectionResult) => {
    //we need a test function for finding the given curveIndex;
    const curveIndexTester = isSplineCurve(curveIndex);
    //get the indices for the profile intervals between start and end (inclusive)
    const profileIndices = getProfileIntervalIndices(start, end, profile, false, from);
    const checkPositions = compareSpline !== undefined && curveIsAbove !== undefined;
    //should we append or prepend when the curve is missing from an interval
    const appendCurveIndex = checkPositions ? curveIsAbove : true;
    // make sure that the given curveIndex appears in the
    // intervals referenced by the profileIndices save the
    // last one
    profileIndices.forEach((pi, i) => {
        //get the profile interval
        const profileInterval = profile[pi];
        //find the index of the curve within this interval
        const psi = profileInterval.order.findIndex(curveIndexTester);
        let checkPosition = checkPositions;
        if (i < profileIndices.length - 1) {
            //make sure the curve exists within the interval
            if (psi === -1) {
                //it does not exist so add it
                if (appendCurveIndex) {
                    //append
                    profileInterval.order.push(curveIndex);
                }
                else {
                    //prepend
                    profileInterval.order.unshift(curveIndex);
                }
                //no need to check position anymore
                checkPosition = false;
            }
            else {
                //it exists so replace (same spline but different curve perhaps)
                profileInterval.order[psi] = curveIndex;
            }
            //if this was an actual intersection, update the interval with this intersection
            if (intersectionResult) {
                const intersectionIndex = intersectionResult.points.findIndex((point) => point.x === profileInterval.x);
                if (intersectionIndex != -1) {
                    //proper intersection
                    profileInterval.intersections.push({
                        curves: intersectionResult.curves,
                        t0: intersectionResult.t0[intersectionIndex],
                        y: intersectionResult.points[intersectionIndex].y,
                    });
                }
            }
        }
        else {
            if (isLast) {
                //last index: make sure curveIndex does NOT exists
                if (psi !== -1) {
                    //it exists so remove the curve from the interval
                    profileInterval.order.splice(psi, 1)[0];
                }
            }
            //no need to check position in last interval
            checkPosition = false;
        }
        if (checkPosition) {
            //check if the spline where the curve belongs to is above
            //the given compareSpline when curveIsAbove is true and vice versa
            //when this is not the case -> swap them
            //first, get the position of the curve where to compare to
            const csi = profileInterval.order.findIndex(isSplineCurve(compareSpline));
            //do the check
            const curveIsActuallyAbove = psi > csi;
            if (curveIsAbove !== curveIsActuallyAbove) {
                //the curve is at the wrong position
                //remove it from current position
                profileInterval.order.splice(psi, 1)[0];
                //move it to the correct position
                profileInterval.order.splice(csi, 0, curveIndex);
            }
        }
    });
    return profileIndices[0];
};
